import React, { useEffect, useState, useCallback } from 'react';
import { Landmark, Users, FileText, Receipt, Printer, Plus, Trash2, CheckCircle2, Loader2, Pencil, X, Percent, Paperclip, Mail } from 'lucide-react';
import {
  adminGetPayrollSummary, adminGetPayrollBusiness, adminSetPayrollBusiness,
  adminGetStaff, adminCreateStaff, adminUpdateStaff, adminDeleteStaff,
  adminGetPayRuns, adminCreatePayRun, adminMarkPayRunPaid, adminDeletePayRun,
  adminGetInvoices, adminCreateInvoice, adminSetInvoiceStatus, adminDeleteInvoice,
  adminGetBills, adminCreateBill, adminUpdateBill, adminCreateBillFromUpload, adminSetBillStatus, adminDeleteBill, adminGetSupplierDefaults, adminGetSpendTrend,
  adminGetBas, adminUploadBillReceipt, adminUploadInvoiceAttachment, adminRunReceiptInbox, adminGetTaxPack, adminDownloadTaxPackCsv,
} from '../../lib/api';
import { printPayslip, printPayslips, printInvoice, printBas, printTaxPack } from './payrollPrint';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_STAFF = { name: '', email: '', role: '', payType: 'hourly', hourlyRate: 0, annualSalary: 0, superRate: 12, bankAccountName: '', bankBSB: '', bankAccountNumber: '', active: true };
const EMPTY_INV = { clientName: '', clientEmail: '', clientAddress: '', issueDate: '', dueDate: '', gstEnabled: true, notes: '', costCentre: '', items: [{ description: '', qty: 1, unitPrice: 0 }] };
const EMPTY_BILL = { supplier: '', reference: '', description: '', category: 'General', costCentre: '', amount: 0, gstIncluded: true, dueDate: '', billDate: '' };

const inputCls = 'bg-alp-dark border-alp-line text-alp-cream';
const selectCls = 'bg-alp-dark border border-alp-line text-alp-cream rounded-md px-3 py-2 text-sm w-full';
const btnGold = 'inline-flex items-center gap-1.5 bg-alp-gold text-alp-black px-4 py-2 text-xs tracking-[0.12em] uppercase font-semibold rounded hover:bg-alp-goldlight transition-colors disabled:opacity-50';
const btnGhost = 'inline-flex items-center gap-1.5 border border-alp-line text-alp-cream px-3 py-1.5 text-xs rounded hover:border-alp-gold hover:text-alp-gold transition-colors';
const pill = (txt, cls) => <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cls}`}>{txt}</span>;

const STATUS_PILLS = {
  draft: 'bg-zinc-700/60 text-zinc-300', sent: 'bg-amber-500/15 text-amber-300',
  paid: 'bg-emerald-500/15 text-emerald-300', unpaid: 'bg-amber-500/15 text-amber-300',
  review: 'bg-sky-500/15 text-sky-300',
};

export default function PayrollPanel() {
  const { toast } = useToast();
  const [sub, setSub] = useState('runs');
  const [summary, setSummary] = useState(null);
  const [biz, setBiz] = useState({ businessName: '', businessABN: '', businessAddress: '' });
  const [staff, setStaff] = useState([]);
  const [runs, setRuns] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    Promise.all([
      adminGetPayrollSummary(), adminGetPayrollBusiness(), adminGetStaff(),
      adminGetPayRuns(), adminGetInvoices(), adminGetBills(),
    ]).then(([s, b, st, r, inv, bl]) => {
      setSummary(s); setBiz(b); setStaff(st); setRuns(r); setInvoices(inv); setBills(bl);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  if (loading) return <div className="py-20 text-center text-alp-gray"><Loader2 className="animate-spin inline" size={20} /></div>;

  const subTabs = [
    ['runs', 'Pay Runs', FileText], ['staff', 'Staff', Users],
    ['invoices', 'Invoices', Receipt], ['bills', 'Spendings', Landmark],
    ['bas', 'BAS', Percent], ['business', 'Business', Pencil],
  ];

  return (
    <div data-testid="payroll-panel">
      <div className="mb-6">
        <h2 className="font-serif text-2xl text-alp-cream flex items-center gap-2"><Landmark size={20} className="text-alp-gold" /> Payroll &amp; Invoicing</h2>
        <p className="text-alp-gray text-sm mt-1">Pay runs with PAYG + 12% super, tax invoices with GST, and bills &mdash; all in-house.</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="payroll-summary">
          {[['Gross paid YTD', fmt(summary.ytdGross), 'text-alp-cream'],
            ['PAYG withheld YTD', fmt(summary.ytdTax), 'text-amber-300'],
            ['Super YTD', fmt(summary.ytdSuper), 'text-alp-cream'],
            ['Invoices outstanding', fmt(summary.outstandingInvoices), 'text-alp-gold'],
            ['Bills unpaid', fmt(summary.unpaidBills), 'text-red-300'],
            [`Crew payouts pending (${summary.pendingCrewPayoutCount})`, fmt(summary.pendingCrewPayouts), 'text-emerald-300'],
          ].map(([l, v, c]) => (
            <div key={l} className="bg-alp-card/60 border border-alp-line rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-alp-gray">{l}</div>
              <div className={`text-lg font-semibold mt-0.5 ${c}`}>{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6" data-testid="payroll-subtabs">
        {subTabs.map(([k, l, I]) => {
          const reviewCount = k === 'bills' ? bills.filter((b) => b.status === 'review').length : 0;
          return (
            <button key={k} data-testid={`payroll-subtab-${k}`} onClick={() => setSub(k)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs tracking-[0.12em] uppercase font-semibold border transition-colors ${sub === k ? 'bg-alp-gold text-alp-black border-alp-gold' : 'bg-alp-card/60 text-alp-cream border-alp-line hover:border-alp-gold/60'}`}>
              <I size={13} /> {l}
              {reviewCount > 0 && (
                <span data-testid="spendings-review-badge" className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${sub === k ? 'bg-alp-black text-sky-300' : 'bg-sky-500/90 text-white'}`}>{reviewCount}</span>
              )}
            </button>
          );
        })}
      </div>

      {sub === 'staff' && <StaffTab staff={staff} reload={reload} toast={toast} />}
      {sub === 'runs' && <RunsTab runs={runs} staff={staff} biz={biz} reload={reload} toast={toast} />}
      {sub === 'invoices' && <InvoicesTab invoices={invoices} biz={biz} reload={reload} toast={toast} />}
      {sub === 'bills' && <BillsTab bills={bills} reload={reload} toast={toast} />}
      {sub === 'bas' && <BasTab biz={biz} />}
      {sub === 'business' && <BusinessTab biz={biz} setBiz={setBiz} toast={toast} />}
    </div>
  );
}

function StaffTab({ staff, reload, toast }) {
  const [form, setForm] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name.trim()) return toast({ title: 'Name is required', variant: 'destructive' });
    try {
      if (form.id) await adminUpdateStaff(form.id, form); else await adminCreateStaff(form);
      toast({ title: form.id ? 'Staff updated' : 'Staff added' });
      setForm(null); reload();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Save failed', variant: 'destructive' }); }
  };
  return (
    <div>
      <button data-testid="add-staff-btn" className={btnGold} onClick={() => setForm({ ...EMPTY_STAFF })}><Plus size={14} /> Add staff member</button>
      {form && (
        <div className="bg-alp-card/60 border border-alp-gold/40 rounded-lg p-5 mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="staff-form">
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Full name *</label><Input data-testid="staff-name" className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Role</label><Input data-testid="staff-role" className={inputCls} value={form.role} onChange={(e) => set('role', e.target.value)} /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Email</label><Input className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Pay type</label>
            <select data-testid="staff-paytype" className={selectCls} value={form.payType} onChange={(e) => set('payType', e.target.value)}>
              <option value="hourly">Hourly</option><option value="salary">Salary</option>
            </select></div>
          {form.payType === 'hourly'
            ? <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Hourly rate ($)</label><Input data-testid="staff-rate" type="number" className={inputCls} value={form.hourlyRate} onChange={(e) => set('hourlyRate', Number(e.target.value))} /></div>
            : <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Annual salary ($)</label><Input data-testid="staff-salary" type="number" className={inputCls} value={form.annualSalary} onChange={(e) => set('annualSalary', Number(e.target.value))} /></div>}
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Super rate (%)</label><Input type="number" className={inputCls} value={form.superRate} onChange={(e) => set('superRate', Number(e.target.value))} /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Bank account name</label><Input className={inputCls} value={form.bankAccountName} onChange={(e) => set('bankAccountName', e.target.value)} /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">BSB</label><Input className={inputCls} value={form.bankBSB} onChange={(e) => set('bankBSB', e.target.value)} /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Account number</label><Input className={inputCls} value={form.bankAccountNumber} onChange={(e) => set('bankAccountNumber', e.target.value)} /></div>
          <div className="sm:col-span-2 lg:col-span-3 flex gap-2">
            <button data-testid="save-staff-btn" className={btnGold} onClick={save}>Save</button>
            <button className={btnGhost} onClick={() => setForm(null)}><X size={13} /> Cancel</button>
          </div>
        </div>
      )}
      <div className="mt-5 border border-alp-line rounded-lg overflow-hidden divide-y divide-alp-line/60 bg-alp-card/30" data-testid="staff-list">
        {staff.length === 0 && <p className="text-alp-gray py-10 text-center text-sm">No staff yet. Add your first team member above.</p>}
        {staff.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="flex-1 min-w-[160px]">
              <div className="text-alp-cream font-medium">{s.name} {!s.active && pill('inactive', 'bg-zinc-700/60 text-zinc-400')}</div>
              <div className="text-alp-gray text-xs">{s.role || '—'} · {s.payType === 'salary' ? `${fmt(s.annualSalary)}/yr` : `${fmt(s.hourlyRate)}/hr`} · super {s.superRate}%</div>
            </div>
            <div className="text-alp-gray text-xs hidden sm:block">{s.bankAccountNumber ? `BSB ${s.bankBSB} · ${s.bankAccountNumber}` : 'No bank details'}</div>
            <button className={btnGhost} data-testid={`edit-staff-${s.id}`} onClick={() => setForm({ ...EMPTY_STAFF, ...s })}><Pencil size={12} /> Edit</button>
            <button className={`${btnGhost} hover:border-red-400 hover:text-red-400`} onClick={async () => { if (window.confirm(`Remove ${s.name}?`)) { await adminDeleteStaff(s.id); reload(); } }}><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RunsTab({ runs, staff, biz, reload, toast }) {
  const [draft, setDraft] = useState(null);
  const active = staff.filter((s) => s.active !== false);
  const start = () => setDraft({ periodStart: today(), periodEnd: today(), frequency: 'weekly', picked: {} });
  const togglePick = (id) => setDraft((d) => ({ ...d, picked: { ...d.picked, [id]: d.picked[id] ? undefined : { hours: 0 } } }));
  const setHours = (id, hours) => setDraft((d) => ({ ...d, picked: { ...d.picked, [id]: { ...d.picked[id], hours: Number(hours) } } }));
  const create = async () => {
    const lines = Object.entries(draft.picked).filter(([, v]) => v).map(([staffId, v]) => ({ staffId, hours: v.hours || 0 }));
    if (!lines.length) return toast({ title: 'Tick at least one staff member', variant: 'destructive' });
    try {
      await adminCreatePayRun({ periodStart: draft.periodStart, periodEnd: draft.periodEnd, frequency: draft.frequency, lines });
      toast({ title: 'Pay run created (draft)' });
      setDraft(null); reload();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed to create run', variant: 'destructive' }); }
  };
  return (
    <div>
      {!draft && <button data-testid="new-payrun-btn" className={btnGold} onClick={start}><Plus size={14} /> New pay run</button>}
      {draft && (
        <div className="bg-alp-card/60 border border-alp-gold/40 rounded-lg p-5 mt-1" data-testid="payrun-form">
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Period start</label><Input data-testid="payrun-start" type="date" className={inputCls} value={draft.periodStart} onChange={(e) => setDraft({ ...draft, periodStart: e.target.value })} /></div>
            <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Period end</label><Input data-testid="payrun-end" type="date" className={inputCls} value={draft.periodEnd} onChange={(e) => setDraft({ ...draft, periodEnd: e.target.value })} /></div>
            <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Frequency</label>
              <select data-testid="payrun-frequency" className={selectCls} value={draft.frequency} onChange={(e) => setDraft({ ...draft, frequency: e.target.value })}>
                <option value="weekly">Weekly</option><option value="fortnightly">Fortnightly</option><option value="monthly">Monthly</option>
              </select></div>
          </div>
          {active.length === 0 ? <p className="text-alp-gray text-sm">Add staff first (Staff tab).</p> : active.map((s) => (
            <div key={s.id} className="flex items-center gap-3 py-2 border-b border-alp-line/50 last:border-0">
              <input type="checkbox" data-testid={`payrun-pick-${s.id}`} checked={!!draft.picked[s.id]} onChange={() => togglePick(s.id)} className="accent-[#D4B483] w-4 h-4" />
              <div className="flex-1">
                <span className="text-alp-cream text-sm">{s.name}</span>
                <span className="text-alp-gray text-xs ml-2">{s.payType === 'salary' ? `${fmt(s.annualSalary)}/yr` : `${fmt(s.hourlyRate)}/hr`}</span>
              </div>
              {draft.picked[s.id] && s.payType === 'hourly' && (
                <div className="flex items-center gap-1.5">
                  <Input data-testid={`payrun-hours-${s.id}`} type="number" className={`${inputCls} w-24`} placeholder="Hours" value={draft.picked[s.id].hours || ''} onChange={(e) => setHours(s.id, e.target.value)} />
                  <span className="text-alp-gray text-xs">hrs</span>
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-2 mt-4">
            <button data-testid="create-payrun-btn" className={btnGold} onClick={create}>Calculate &amp; create run</button>
            <button className={btnGhost} onClick={() => setDraft(null)}><X size={13} /> Cancel</button>
          </div>
          <p className="text-alp-gray text-[11px] mt-3">PAYG is estimated from the ATO Schedule 1 formula (tax-free threshold). Super is calculated on top of gross.</p>
        </div>
      )}
      <div className="mt-5 space-y-3" data-testid="payrun-list">
        {runs.length === 0 && <p className="text-alp-gray py-10 text-center text-sm">No pay runs yet.</p>}
        {runs.map((r) => (
          <div key={r.id} className="bg-alp-card/40 border border-alp-line rounded-lg overflow-hidden">
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-alp-line/50">
              <span className="text-alp-gold font-semibold text-sm">{r.runNumber}</span>
              <span className="text-alp-gray text-xs">{r.periodStart} → {r.periodEnd} · {r.frequency}</span>
              {pill(r.status, STATUS_PILLS[r.status] || STATUS_PILLS.draft)}
              <div className="flex-1" />
              <button className={btnGhost} data-testid={`print-run-${r.id}`} onClick={() => printPayslips(r, biz)}><Printer size={12} /> Payslips</button>
              {r.status !== 'paid' && <>
                <button className={`${btnGhost} hover:border-emerald-400 hover:text-emerald-400`} data-testid={`pay-run-${r.id}`} onClick={async () => { await adminMarkPayRunPaid(r.id); toast({ title: `${r.runNumber} marked paid` }); reload(); }}><CheckCircle2 size={12} /> Mark paid</button>
                <button className={`${btnGhost} hover:border-red-400 hover:text-red-400`} onClick={async () => { if (window.confirm('Delete this draft run?')) { await adminDeletePayRun(r.id); reload(); } }}><Trash2 size={12} /></button>
              </>}
            </div>
            <div className="px-4 py-2">
              <div className="hidden sm:grid grid-cols-6 text-[10px] uppercase tracking-wider text-alp-gray py-1">
                <span className="col-span-2">Staff</span><span className="text-right">Gross</span><span className="text-right">PAYG withheld</span><span className="text-right">Super</span><span className="text-right">Net</span>
              </div>
              {(r.lines || []).map((l, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-6 text-sm py-1.5 border-t border-alp-line/40 items-center">
                  <span className="sm:col-span-2 text-alp-cream">{l.name}
                    <button className="ml-2 text-alp-gold/70 hover:text-alp-gold" title="Print payslip" onClick={() => printPayslip(r, l, biz)}><Printer size={11} className="inline" /></button>
                  </span>
                  <span className="text-right text-alp-cream">{fmt(l.gross)}</span>
                  <span className="text-right text-amber-300">−{fmt(l.tax)}</span>
                  <span className="text-right text-alp-gray">{fmt(l.super)}</span>
                  <span className="text-right text-emerald-300 font-semibold">{fmt(l.net)}</span>
                </div>
              ))}
              <div className="grid grid-cols-2 sm:grid-cols-6 text-sm py-2 border-t border-alp-gold/30 font-semibold">
                <span className="sm:col-span-2 text-alp-gold">Totals</span>
                <span className="text-right text-alp-cream">{fmt(r.totalGross)}</span>
                <span className="text-right text-amber-300">−{fmt(r.totalTax)}</span>
                <span className="text-right text-alp-gray">{fmt(r.totalSuper)}</span>
                <span className="text-right text-emerald-300">{fmt(r.totalNet)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InvoicesTab({ invoices, biz, reload, toast }) {
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState('');
  const attachFile = async (id, file) => {
    if (!file) return;
    setUploading(id);
    try {
      await adminUploadInvoiceAttachment(id, file);
      toast({ title: 'File attached' });
      reload();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Upload failed', variant: 'destructive' }); }
    finally { setUploading(''); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const subtotal = form ? form.items.reduce((a, i) => a + Number(i.qty || 0) * Number(i.unitPrice || 0), 0) : 0;
  const gst = form?.gstEnabled ? subtotal * 0.1 : 0;
  const save = async () => {
    if (!form.clientName.trim()) return toast({ title: 'Client name is required', variant: 'destructive' });
    if (!form.items.some((i) => i.description.trim())) return toast({ title: 'Add at least one line item', variant: 'destructive' });
    try {
      const inv = await adminCreateInvoice({ ...form, items: form.items.filter((i) => i.description.trim()) });
      toast({ title: `${inv.invoiceNumber} created` });
      setForm(null); reload();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div>
      <button data-testid="new-invoice-btn" className={btnGold} onClick={() => setForm({ ...EMPTY_INV, issueDate: today(), items: [{ description: '', qty: 1, unitPrice: 0 }] })}><Plus size={14} /> New invoice</button>
      {form && (
        <div className="bg-alp-card/60 border border-alp-gold/40 rounded-lg p-5 mt-4" data-testid="invoice-form">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Client name *</label><Input data-testid="invoice-client" className={inputCls} value={form.clientName} onChange={(e) => set('clientName', e.target.value)} /></div>
            <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Client email</label><Input className={inputCls} value={form.clientEmail} onChange={(e) => set('clientEmail', e.target.value)} /></div>
            <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Issue date</label><Input type="date" className={inputCls} value={form.issueDate} onChange={(e) => set('issueDate', e.target.value)} /></div>
            <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Due date</label><Input data-testid="invoice-due" type="date" className={inputCls} value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} /></div>
            <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-wider text-alp-gray">Client address</label><Input className={inputCls} value={form.clientAddress} onChange={(e) => set('clientAddress', e.target.value)} /></div>
            <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Cost centre</label>
              <Input data-testid="invoice-cost-centre" className={inputCls} list="cost-centres" placeholder="e.g. Wholesale" value={form.costCentre} onChange={(e) => set('costCentre', e.target.value)} />
              <datalist id="cost-centres">
                {[...new Set(['Wholesale', 'Retail', 'Research', 'Consulting', ...invoices.map((i) => i.costCentre).filter(Boolean)])].map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Notes</label><Input className={inputCls} value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
          </div>
          {form.items.map((it, i) => (
            <div key={i} className="flex gap-2 items-center mb-2">
              <Input data-testid={`invoice-item-desc-${i}`} className={`${inputCls} flex-1`} placeholder="Description" value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} />
              <Input data-testid={`invoice-item-qty-${i}`} type="number" className={`${inputCls} w-20`} placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, 'qty', Number(e.target.value))} />
              <Input data-testid={`invoice-item-price-${i}`} type="number" className={`${inputCls} w-28`} placeholder="Unit $" value={it.unitPrice} onChange={(e) => setItem(i, 'unitPrice', Number(e.target.value))} />
              <button className="text-alp-gray hover:text-red-400" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }))}><Trash2 size={14} /></button>
            </div>
          ))}
          <button className={btnGhost} onClick={() => setForm((f) => ({ ...f, items: [...f.items, { description: '', qty: 1, unitPrice: 0 }] }))}><Plus size={12} /> Line</button>
          <div className="flex flex-wrap items-center gap-4 mt-4">
            <label className="flex items-center gap-2 text-sm text-alp-cream">
              <input type="checkbox" data-testid="invoice-gst" checked={form.gstEnabled} onChange={(e) => set('gstEnabled', e.target.checked)} className="accent-[#D4B483] w-4 h-4" /> Add 10% GST
            </label>
            <span className="text-alp-gray text-sm">Subtotal {fmt(subtotal)}{form.gstEnabled ? ` + GST ${fmt(gst)}` : ''} = <span className="text-alp-gold font-semibold">{fmt(subtotal + gst)}</span></span>
            <div className="flex-1" />
            <button data-testid="save-invoice-btn" className={btnGold} onClick={save}>Create invoice</button>
            <button className={btnGhost} onClick={() => setForm(null)}><X size={13} /> Cancel</button>
          </div>
        </div>
      )}
      <div className="mt-5 border border-alp-line rounded-lg overflow-hidden divide-y divide-alp-line/60 bg-alp-card/30" data-testid="invoice-list">
        {invoices.length === 0 && <p className="text-alp-gray py-10 text-center text-sm">No invoices yet.</p>}
        {invoices.map((inv) => (
          <div key={inv.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <span className="text-alp-gold font-semibold text-sm">{inv.invoiceNumber}</span>
            <div className="flex-1 min-w-[140px]">
              <div className="text-alp-cream text-sm">{inv.clientName} {inv.costCentre && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-alp-line text-alp-gray ml-1">{inv.costCentre}</span>}</div>
              <div className="text-alp-gray text-xs">Issued {inv.issueDate}{inv.dueDate ? ` · due ${inv.dueDate}` : ''}</div>
            </div>
            <span className="text-alp-cream font-semibold">{fmt(inv.total)}</span>
            {pill(inv.status, STATUS_PILLS[inv.status])}
            <button className={btnGhost} data-testid={`print-invoice-${inv.id}`} onClick={() => printInvoice(inv, biz)}><Printer size={12} /> Print</button>
            {inv.attachmentUrl ? (
              <a href={inv.attachmentUrl} target="_blank" rel="noreferrer" data-testid={`invoice-file-link-${inv.id}`} className={btnGhost}><Paperclip size={12} /> File</a>
            ) : (
              <label className={`${btnGhost} cursor-pointer`} data-testid={`invoice-file-attach-${inv.id}`}>
                {uploading === inv.id ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />} Attach file
                <input type="file" accept="image/*,.pdf,.heic" className="hidden" onChange={(e) => attachFile(inv.id, e.target.files?.[0])} />
              </label>
            )}
            {inv.status === 'draft' && <button className={btnGhost} onClick={async () => { await adminSetInvoiceStatus(inv.id, 'sent'); reload(); }}>Mark sent</button>}
            {inv.status !== 'paid' && <button className={`${btnGhost} hover:border-emerald-400 hover:text-emerald-400`} data-testid={`invoice-paid-${inv.id}`} onClick={async () => { await adminSetInvoiceStatus(inv.id, 'paid'); toast({ title: `${inv.invoiceNumber} paid` }); reload(); }}><CheckCircle2 size={12} /> Paid</button>}
            <button className={`${btnGhost} hover:border-red-400 hover:text-red-400`} onClick={async () => { if (window.confirm(`Delete ${inv.invoiceNumber}?`)) { await adminDeleteInvoice(inv.id); reload(); } }}><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BillsTab({ bills, reload, toast }) {
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState('');
  const [checking, setChecking] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const checkInbox = async () => {
    setChecking(true);
    try {
      const res = await adminRunReceiptInbox();
      if (res.error) toast({ title: 'Could not reach the mailbox', description: res.error, variant: 'destructive' });
      else if (res.created?.length) { toast({ title: `${res.created.length} receipt(s) logged from email` }); reload(); }
      else toast({ title: 'No new receipt emails found' });
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Inbox check failed', variant: 'destructive' }); }
    finally { setChecking(false); }
  };
  const save = async () => {
    if (!form.supplier.trim()) return toast({ title: 'Supplier is required', variant: 'destructive' });
    try {
      if (form.id) {
        await adminUpdateBill(form.id, form);
        toast({ title: form.status === 'review' ? 'Approved — now counted in BAS & P&L' : 'Spending updated' });
      } else {
        await adminCreateBill(form);
        toast({ title: 'Spending recorded' });
      }
      setForm(null); reload();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const [aiUploading, setAiUploading] = useState(false);
  const newFromUpload = async (file) => {
    if (!file) return;
    setAiUploading(true);
    try {
      const b = await adminCreateBillFromUpload(file);
      toast({ title: `AI pre-filled "${b.supplier}" — check & approve` });
      setForm(b);
      reload();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Upload failed', variant: 'destructive' }); }
    finally { setAiUploading(false); }
  };
  const attachReceipt = async (billId, file) => {
    if (!file) return;
    setUploading(billId);
    try {
      await adminUploadBillReceipt(billId, file);
      toast({ title: 'Receipt attached' });
      reload();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Receipt upload failed', variant: 'destructive' }); }
    finally { setUploading(''); }
  };
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="new-bill-btn" className={btnGold} onClick={() => setForm({ ...EMPTY_BILL, billDate: today() })}><Plus size={14} /> Record a spending</button>
        <label className={`${btnGhost} cursor-pointer border-alp-gold/50 text-alp-gold`} data-testid="bill-ai-upload">
          {aiUploading ? <Loader2 size={13} className="animate-spin" /> : <Paperclip size={13} />} {aiUploading ? 'AI reading it...' : 'New from photo/PDF'}
          <input type="file" accept="image/*,.pdf,.heic" className="hidden" onChange={(e) => { newFromUpload(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        <button data-testid="check-inbox-btn" className={btnGhost} disabled={checking} onClick={checkInbox}>
          {checking ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Check email for receipts
        </button>
        <span className="text-alp-gray text-[11px]">Email a photo/PDF to the store email with “receipt” or “invoice” in the subject — it's logged here automatically every 30 min.</span>
      </div>
      {form && (
        <div className="bg-alp-card/60 border border-alp-gold/40 rounded-lg p-5 mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bill-form">
          {form.status === 'review' && (
            <div className="sm:col-span-2 lg:col-span-3 flex items-center gap-2 text-sky-300 text-xs">
              {pill('review', STATUS_PILLS.review)} AI pre-filled from your upload — check the numbers, then Approve.
              {form.receiptUrl && <a href={form.receiptUrl} target="_blank" rel="noreferrer" className="underline text-alp-gold">View the file</a>}
            </div>
          )}
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Supplier *</label><Input data-testid="bill-supplier" className={inputCls} value={form.supplier} onChange={(e) => set('supplier', e.target.value)}
            onBlur={async () => {
              const s = form.supplier?.trim();
              if (!s || form.id) return;
              try {
                const d = await adminGetSupplierDefaults(s);
                if (d.category || d.costCentre) {
                  setForm((f) => ({
                    ...f,
                    category: (!f.category || f.category === 'General') && d.category ? d.category : f.category,
                    costCentre: f.costCentre || d.costCentre || '',
                  }));
                  toast({ title: `Known supplier — filled your usual category${d.costCentre ? ' & cost centre' : ''}` });
                }
              } catch (e) {}
            }} /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Reference / invoice #</label><Input className={inputCls} value={form.reference} onChange={(e) => set('reference', e.target.value)} /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Category</label><Input className={inputCls} value={form.category} onChange={(e) => set('category', e.target.value)} /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Cost centre</label><Input data-testid="bill-cost-centre" className={inputCls} placeholder="e.g. Retail, Wholesale" value={form.costCentre || ''} onChange={(e) => set('costCentre', e.target.value)} /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Amount ($ incl GST)</label><Input data-testid="bill-amount" type="number" className={inputCls} value={form.amount} onChange={(e) => set('amount', Number(e.target.value))} /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Date of spend</label><Input data-testid="bill-date" type="date" className={inputCls} value={form.billDate} onChange={(e) => set('billDate', e.target.value)} /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Due date</label><Input type="date" className={inputCls} value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} /></div>
          <label className="flex items-center gap-2 text-sm text-alp-cream mt-5">
            <input type="checkbox" checked={form.gstIncluded} onChange={(e) => set('gstIncluded', e.target.checked)} className="accent-[#D4B483] w-4 h-4" /> Includes GST
          </label>
          <div className="sm:col-span-2 lg:col-span-3 flex gap-2">
            <button data-testid="save-bill-btn" className={btnGold} onClick={save}>{form.id ? (form.status === 'review' ? 'Approve & save' : 'Save changes') : 'Save bill'}</button>
            <button className={btnGhost} onClick={() => setForm(null)}><X size={13} /> Cancel</button>
          </div>
        </div>
      )}
      <div className="mt-5 border border-alp-line rounded-lg overflow-hidden divide-y divide-alp-line/60 bg-alp-card/30" data-testid="bill-list">
        {bills.length === 0 && <p className="text-alp-gray py-10 text-center text-sm">No bills recorded.</p>}
        {bills.map((b) => (
          <div key={b.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="flex-1 min-w-[140px]">
              <div className="text-alp-cream text-sm">{b.supplier} {b.reference && <span className="text-alp-gray text-xs">· {b.reference}</span>}</div>
              <div className="text-alp-gray text-xs">{b.category}{b.costCentre ? ` · ${b.costCentre}` : ''}{b.billDate ? ` · ${b.billDate}` : ''}{b.dueDate ? ` · due ${b.dueDate}` : ''}{b.gstAmount ? ` · GST ${fmt(b.gstAmount)}` : ''}</div>
            </div>
            <span className="text-alp-cream font-semibold">{fmt(b.amount)}</span>
            {pill(b.status, STATUS_PILLS[b.status])}
            {b.receiptUrl ? (
              <a href={b.receiptUrl} target="_blank" rel="noreferrer" data-testid={`bill-receipt-link-${b.id}`} className={btnGhost}><Paperclip size={12} /> Receipt</a>
            ) : (
              <label className={`${btnGhost} cursor-pointer`} data-testid={`bill-receipt-attach-${b.id}`}>
                {uploading === b.id ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />} Attach receipt
                <input type="file" accept="image/*,.pdf,.heic" className="hidden" onChange={(e) => attachReceipt(b.id, e.target.files?.[0])} />
              </label>
            )}
            {b.status === 'review' && <button className={`${btnGhost} border-sky-400/50 text-sky-300 hover:border-sky-300 hover:text-sky-200`} data-testid={`bill-review-${b.id}`} onClick={() => setForm({ ...EMPTY_BILL, ...b })}><Pencil size={12} /> Check &amp; approve</button>}
            {b.status === 'unpaid' && <button className={`${btnGhost} hover:border-emerald-400 hover:text-emerald-400`} data-testid={`bill-paid-${b.id}`} onClick={async () => { await adminSetBillStatus(b.id, 'paid'); reload(); }}><CheckCircle2 size={12} /> Paid</button>}
            <button className={`${btnGhost} hover:border-red-400 hover:text-red-400`} onClick={async () => { if (window.confirm('Delete this bill?')) { await adminDeleteBill(b.id); reload(); } }}><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
      <SpendTrend />
    </div>
  );
}

function SpendTrend() {
  const [trend, setTrend] = useState(null);
  useEffect(() => { adminGetSpendTrend().then(setTrend).catch(() => {}); }, []);
  if (!trend || !trend.rows?.length) return null;
  const short = (m) => new Date(`${m}-02`).toLocaleString('en-AU', { month: 'short' });
  const change = (pct) => {
    if (pct === null || pct === undefined) return <span className="text-alp-gray text-xs">new</span>;
    if (pct > 10) return <span className="text-red-300 text-xs font-semibold">&#9650; +{pct}%</span>;
    if (pct < -10) return <span className="text-emerald-300 text-xs font-semibold">&#9660; {pct}%</span>;
    return <span className="text-alp-gray text-xs">&#8776; {pct > 0 ? '+' : ''}{pct}%</span>;
  };
  return (
    <div className="mt-6 bg-alp-card/60 border border-alp-line rounded-lg p-4" data-testid="spend-trend">
      <div className="grid grid-cols-[1fr_auto_5rem_4.5rem] gap-3 items-center pb-2 border-b border-alp-line/60">
        <span className="text-[10px] uppercase tracking-wider text-alp-gray">Cost centre trend &middot; 6 months</span>
        <div className="flex gap-1 w-[7.5rem]">
          {trend.months.map((m) => <span key={m} className="w-4 text-center text-[9px] text-alp-gray/70">{short(m)[0]}</span>)}
        </div>
        <span className="text-[10px] uppercase tracking-wider text-alp-gray text-right">This mth</span>
        <span className="text-[10px] uppercase tracking-wider text-alp-gray text-right">vs last</span>
      </div>
      {trend.rows.map((r) => {
        const max = Math.max(...r.totals, 1);
        return (
          <div key={r.costCentre} className="grid grid-cols-[1fr_auto_5rem_4.5rem] gap-3 items-center py-2.5 border-b border-alp-line/40 last:border-0" data-testid={`spend-trend-row-${r.costCentre}`}>
            <span className="text-alp-cream text-sm truncate">{r.costCentre}</span>
            <div className="flex gap-1 items-end h-7 w-[7.5rem]">
              {r.totals.map((t, i) => (
                <div key={i} title={`${short(trend.months[i])}: ${fmt(t)}`}
                  className={`w-4 rounded-sm ${t > 0 ? 'bg-alp-gold/70' : 'bg-alp-line/70'}`}
                  style={{ height: `${t > 0 ? Math.max(4, (t / max) * 28) : 2}px` }} />
              ))}
            </div>
            <span className="text-alp-cream text-sm text-right">{fmt(r.thisMonth)}</span>
            <span className="text-right">{change(r.changePct)}</span>
          </div>
        );
      })}
      <p className="text-[10px] text-alp-gray/70 mt-2">Top cost centres by recent spend. Red = up more than 10% on last month.</p>
    </div>
  );
}

function BasTab({ biz }) {
  const [data, setData] = useState(null);
  const [qi, setQi] = useState(0);
  const now = new Date();
  const curFy = now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
  const fyOptions = [curFy, curFy - 1, curFy - 2];
  const [fy, setFy] = useState(curFy);
  const [packBusy, setPackBusy] = useState('');
  const runPack = async (mode) => {
    setPackBusy(mode);
    try {
      if (mode === 'print') {
        const pack = await adminGetTaxPack(fy);
        printTaxPack(pack, biz);
      } else {
        const blob = await adminDownloadTaxPackCsv(fy);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ALP-tax-pack-FY${fy - 1}-${String(fy).slice(2)}.csv`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      }
    } catch (e) { /* toast-less: surfaces nothing on failure here */ }
    finally { setPackBusy(''); }
  };
  useEffect(() => { adminGetBas().then(setData).catch(() => setData({ quarters: [] })); }, []);
  if (!data) return <div className="py-16 text-center text-alp-gray"><Loader2 className="animate-spin inline" size={18} /></div>;
  const q = data.quarters[qi];
  if (!q) return <p className="text-alp-gray py-10 text-center text-sm">No BAS data yet.</p>;
  const rows = [
    ['G1', 'Total sales (GST inclusive)', q.g1Sales, `store ${fmt(q.storeSales)} + invoices ${fmt(q.invoiceSales)} · ${q.orderCount} paid orders`],
    ['1A', 'GST on sales', q.gstOnSales1A, 'sales ÷ 11'],
    ['G11', 'Purchases / spendings', q.purchases, `${q.billCount} recorded spendings`],
    ['1B', 'GST credits on purchases', q.gstOnPurchases1B, 'from your recorded spendings'],
    ['W1', 'Wages paid', q.wagesW1, 'from paid pay runs'],
    ['W2', 'PAYG withheld', q.paygW2, 'from paid pay runs'],
  ];
  return (
    <div data-testid="bas-tab">
      <div className="flex flex-wrap gap-2 mb-5">
        {data.quarters.map((qq, i) => (
          <button key={qq.start} data-testid={`bas-quarter-${i}`} onClick={() => setQi(i)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${i === qi ? 'bg-alp-gold text-alp-black border-alp-gold font-semibold' : 'bg-alp-card/60 text-alp-cream border-alp-line hover:border-alp-gold/60'}`}>
            {qq.label}{i === 0 ? ' · current' : ''}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        {rows.map(([code, label, val, hint]) => (
          <div key={code} className="bg-alp-card/60 border border-alp-line rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-wider text-alp-gray"><span className="text-alp-gold font-bold">{code}</span> · {label}</div>
            <div className="text-xl font-semibold text-alp-cream mt-1">{fmt(val)}</div>
            <div className="text-alp-gray text-[11px] mt-0.5">{hint}</div>
          </div>
        ))}
      </div>
      {q.costCentres && q.costCentres.length > 0 && (
        <div className="bg-alp-card/60 border border-alp-line rounded-lg p-4 mb-5" data-testid="bas-cost-centres">
          <div className="text-[10px] uppercase tracking-wider text-alp-gray mb-1">Invoice income by cost centre</div>
          {q.costCentres.map((c) => (
            <div key={c.costCentre} className="flex justify-between text-sm py-1.5 border-t border-alp-line/40 first:border-0">
              <span className="text-alp-cream">{c.costCentre} <span className="text-alp-gray text-xs">({c.count} invoice{c.count === 1 ? '' : 's'})</span></span>
              <span className="text-alp-gold">{fmt(c.total)}</span>
            </div>
          ))}
        </div>
      )}
      {q.costCentreSpend && q.costCentreSpend.length > 0 && (
        <div className="bg-alp-card/60 border border-alp-line rounded-lg p-4 mb-5" data-testid="bas-cost-centre-spend">
          <div className="text-[10px] uppercase tracking-wider text-alp-gray mb-1">Spending by cost centre</div>
          {q.costCentreSpend.map((c) => (
            <div key={c.costCentre} className="flex justify-between text-sm py-1.5 border-t border-alp-line/40 first:border-0">
              <span className="text-alp-cream">{c.costCentre} <span className="text-alp-gray text-xs">({c.count} · GST {fmt(c.gst)})</span></span>
              <span className="text-alp-gold">{fmt(c.total)}</span>
            </div>
          ))}
        </div>
      )}
      {q.spendingCategories && q.spendingCategories.length > 0 && (
        <div className="bg-alp-card/60 border border-alp-line rounded-lg p-4 mb-5" data-testid="bas-spending-categories">
          <div className="text-[10px] uppercase tracking-wider text-alp-gray mb-3">Spending by category — {fmt(q.purchases)} total</div>
          <div className="space-y-2.5">
            {q.spendingCategories.map((c) => {
              const max = q.spendingCategories[0].total || 1;
              const pct = Math.max(4, Math.round((c.total / max) * 100));
              return (
                <div key={c.category} data-testid={`bas-cat-${c.category}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-alp-cream">{c.category} <span className="text-alp-gray text-xs">({c.count} · GST {fmt(c.gst)})</span></span>
                    <span className="text-alp-gold font-semibold">{fmt(c.total)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-alp-dark border border-alp-line/50 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-alp-gold/70 to-alp-gold transition-all duration-700" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="bg-alp-card/60 border border-alp-gold/40 rounded-lg p-5 flex flex-wrap items-center gap-4" data-testid="bas-net">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-alp-gray">{q.netGst >= 0 ? 'Net GST payable (1A − 1B)' : 'Net GST refund'}</div>
          <div className={`text-2xl font-bold ${q.netGst >= 0 ? 'text-alp-gold' : 'text-emerald-300'}`}>{fmt(Math.abs(q.netGst))}</div>
        </div>
        <div className="flex-1" />
        <button className={btnGold} data-testid="print-bas-btn" onClick={() => printBas(q, biz)}><Printer size={14} /> Print BAS summary</button>
      </div>
      <p className="text-alp-gray text-[11px] mt-4">Estimates from your store records — store prices treated as GST-inclusive (GST = total ÷ 11). Attach receipts to every spending so your 1B credits hold up. Confirm with your accountant before lodging with the ATO.</p>

      <div className="bg-alp-card/60 border border-alp-gold/40 rounded-lg p-5 mt-6 flex flex-wrap items-center gap-4" data-testid="tax-pack-card">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-alp-gold">Yearly Tax Pack</div>
          <p className="text-alp-gray text-xs mt-1">Full financial year — sales, spendings, wages &amp; GST, ready for your accountant.</p>
        </div>
        <select data-testid="tax-pack-fy" className={`${selectCls} w-auto`} value={fy} onChange={(e) => setFy(Number(e.target.value))}>
          {fyOptions.map((y) => <option key={y} value={y}>FY{y - 1}–{String(y).slice(2)}</option>)}
        </select>
        <button className={btnGhost} data-testid="tax-pack-print" disabled={packBusy} onClick={() => runPack('print')}>
          {packBusy === 'print' ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />} Print pack
        </button>
        <button className={btnGold} data-testid="tax-pack-csv" disabled={packBusy} onClick={() => runPack('csv')}>
          {packBusy === 'csv' ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} Download CSV
        </button>
      </div>
    </div>
  );
}

function BusinessTab({ biz, setBiz, toast }) {
  const set = (k, v) => setBiz((b) => ({ ...b, [k]: v }));
  const save = async () => {
    await adminSetPayrollBusiness({ businessName: biz.businessName, businessABN: biz.businessABN, businessAddress: biz.businessAddress });
    toast({ title: 'Business details saved' });
  };
  return (
    <div className="bg-alp-card/60 border border-alp-line rounded-lg p-5 max-w-xl" data-testid="business-form">
      <p className="text-alp-gray text-sm mb-4">Shown on every payslip and tax invoice.</p>
      <div className="space-y-3">
        <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Business name</label><Input data-testid="business-name" className={inputCls} value={biz.businessName} onChange={(e) => set('businessName', e.target.value)} /></div>
        <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">ABN</label><Input data-testid="business-abn" className={inputCls} value={biz.businessABN} onChange={(e) => set('businessABN', e.target.value)} /></div>
        <div><label className="text-[10px] uppercase tracking-wider text-alp-gray">Business address</label><Input className={inputCls} value={biz.businessAddress} onChange={(e) => set('businessAddress', e.target.value)} /></div>
        <button data-testid="save-business-btn" className={btnGold} onClick={save}>Save</button>
      </div>
    </div>
  );
}
